// ─── SegmentedControl ───────────────────────────────────────────────────────
// El control de dos o tres opciones excluyentes de la plataforma: fondo gris
// claro, pastilla blanca que se desliza hasta la opción elegida.
//
// La forma sale del catálogo de UI (`UICatalog2`, sección "14. Segmented
// Control"), que hasta ahora estaba copiada a mano en cada pantalla que la
// necesitaba. Acá vive una sola vez para que todas usen la misma: el mismo
// gris, el mismo radio, la misma sombra y la misma animación.
//
// Sirve para elegir entre ÁMBITOS o MODOS que se excluyen y son pocos. Para
// filtrar por categoría —donde las opciones pueden ser varias y crecer— la
// plataforma usa las pastillas redondas, que es otra cosa.

import React from 'react';

/** El aire entre el borde del carril y la pastilla, en px. Tiene que ser el
 *  MISMO número que la clase `p-1` del contenedor (4px) y que `top-1 bottom-1`
 *  de la pastilla: el cálculo de abajo lo descuenta a mano, así que si cambia
 *  la clase hay que cambiarlo acá o la pastilla vuelve a quedar descentrada. */
const PADDING_DEL_CARRIL = 4;

export type OpcionDelSegmento<T extends string> = {
  id: T;
  label: string;
};

export type SegmentedControlProps<T extends string> = {
  opciones: ReadonlyArray<OpcionDelSegmento<T>>;
  valor: T;
  onCambio: (id: T) => void;
  /** Ocupa todo el ancho disponible. Default: true. */
  ancho?: 'completo' | 'contenido';
  /** Etiqueta para lectores de pantalla. */
  ariaLabel?: string;
  className?: string;
};

export function SegmentedControl<T extends string>({
  opciones,
  valor,
  onCambio,
  ancho = 'completo',
  ariaLabel,
  className = '',
}: SegmentedControlProps<T>) {
  const indice = Math.max(
    0,
    opciones.findIndex(o => o.id === valor),
  );
  const cantidad = Math.max(1, opciones.length);

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`relative flex bg-[#F2F2F7] rounded-[9px] p-1 ${
        ancho === 'completo' ? 'w-full' : 'inline-flex'
      } ${className}`}
    >
      {/* La pastilla es UN elemento que se mueve, no un fondo por botón: así el
          cambio de opción se anima en vez de saltar. Va detrás del texto
          (`z-10` en los botones) para no taparlo.

          El ancho y el `left` descuentan el padding del contenedor A MANO
          porque un `absolute` en % se mide contra el padding-box, que ya lo
          incluye: con `width: 50% - 2px` la última opción terminaba a 1px del
          borde mientras arriba y abajo tenía 4, y la pastilla se veía pegada
          al costado. Repartiendo `100% - 2*PADDING` entre las opciones, el aire
          queda igual en los cuatro lados. */}
      <div
        aria-hidden
        className="absolute top-1 bottom-1 bg-white rounded-[7px] shadow-[0_1px_3px_rgba(0,0,0,0.08)] transition-all duration-300 ease-out"
        style={{
          width: `calc((100% - ${PADDING_DEL_CARRIL * 2}px) / ${cantidad})`,
          left: `calc(${PADDING_DEL_CARRIL}px + (100% - ${PADDING_DEL_CARRIL * 2}px) * ${indice} / ${cantidad})`,
        }}
      />
      {opciones.map(o => {
        const activa = o.id === valor;
        return (
          <button
            key={o.id}
            type="button"
            role="tab"
            aria-selected={activa}
            onClick={() => onCambio(o.id)}
            className={`relative z-10 flex-1 text-[13px] font-medium py-1.5 px-2 text-center transition-colors truncate ${
              activa ? 'text-black/90' : 'text-[#3C3C43]/60 hover:text-[#3C3C43]/80'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
